import {createFeature, createReducer, createSelector, on} from '@ngrx/store';
import {EntityState, EntityAdapter, createEntityAdapter} from '@ngrx/entity';
import {DepotsActions, DepotsAPIActions, DepotsChannelActions} from "./depots.actions";
import {uniq} from "lodash";
import {DepotModel} from "../models/depot.model";

export interface DepotState extends EntityState<DepotModel> {
  cursor: string;
  isLoading: boolean;
  isInfiniteScrollLoading: boolean;
  infiniteScrollDepotIds: string[]
}

const adapter: EntityAdapter<DepotModel> = createEntityAdapter<DepotModel>();

const initialState: DepotState = adapter.getInitialState(
  {
    cursor: null,
    isLoading: false,
    isInfiniteScrollLoading: false,
    infiniteScrollDepotIds: []
  }
);

const reducer = createReducer(
  initialState,

  on(DepotsChannelActions.notificationReceived, (state, {notification}) => {
    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      return adapter.setOne(notification, state);
    } else {
      return state;
    }

  }),

  on(DepotsAPIActions.getDepotSuccess, (state, {depot}) =>
    adapter.setOne(depot, state)
  ),

  on(DepotsAPIActions.updateDepotSuccess, (state, {depot}) =>
    adapter.updateOne({
      id: depot.id,
      changes: {...depot}
    }, state)
  ),


  on(DepotsAPIActions.deleteDepotSuccess, (state, {depot}) =>
    adapter.removeOne(depot.id, state)
  ),

  on(DepotsAPIActions.getInfiniteScrollDepots, (state) => ({...state, isInfiniteScrollLoading: true})),

  on(DepotsAPIActions.getInfiniteScrollDepotsSuccess, (state, {depots, cursor}) => {
    return adapter.setMany(depots,
      {
        ...state,
        infiniteScrollDepotIds: uniq([...state.infiniteScrollDepotIds, ...depots.map(s => s.id)]),
        cursor: cursor,
        isInfiniteScrollLoading: false
      })
  }),

  on(DepotsAPIActions.getInfiniteScrollDepotsFailure, (state, {error}) => ({...state, isInfiniteScrollLoading: false})),


  on(DepotsActions.setInfiniteScrollDepotIds, (state, {depotIds, isAppendFirst}) => {
    let newState = state;
    const currentInfiniteScrollDepotIdsInStore = state.infiniteScrollDepotIds;

    if (isAppendFirst) {
      newState = {
        ...newState,
        infiniteScrollDepotIds: uniq([...depotIds, ...currentInfiniteScrollDepotIdsInStore])
      }
    } else {
      newState = {
        ...newState,
        infiniteScrollDepotIds: uniq([...currentInfiniteScrollDepotIdsInStore, ...depotIds])
      }
    }
    return newState;

  }),

  on(DepotsActions.removeAll, (state) => adapter.removeAll(state)),

  on(DepotsActions.clearStore, () => ({...initialState}))
);

export const depotsFeature = createFeature({
  name: 'Depots',
  reducer,
  extraSelectors: ({
                     selectEntities,
                     selectCursor,
                     selectIsLoading,
                     selectInfiniteScrollDepotIds,
                     selectIsInfiniteScrollLoading
                   }) => ({
    selectDepots: () => createSelector(
      selectEntities,
      (entities) => entities,
    ),
    selectDepot: (id: string) => createSelector(
      selectEntities,
      (entities) => entities[id],
    ),
    selectInfiniteScrollDepots: () => createSelector(
      selectEntities,
      selectInfiniteScrollDepotIds,
      (entities, infiniteScrollDepotIds) => {
        const infiniteScrollDepots = [];
        infiniteScrollDepotIds.forEach(id => {
          if (!!entities[id]) {
            infiniteScrollDepots.push(entities[id]);
          }
        })
        return infiniteScrollDepots;
      },
    ),
    selectCursor: () => createSelector(
      selectCursor,
      (cursor) => cursor
    ),
    selectIsInfiniteScrollLoading: () => createSelector(
      selectIsInfiniteScrollLoading,
      (isLoading) => isLoading
    ),
  }),
});

export const {
  selectIds,
  selectAll,
  selectTotal
} = adapter.getSelectors();
